// Jitter view (owned by the web:charts agent).
//
// Three linked views of timing variability:
//   1. |Δrtt| over time (uPlot) — consecutive-echo round-trip jitter.
//   2. |Δrtt| histogram (canvas).
//   3. Summary bars comparing send-side pacing jitter, receive-side arrival
//      jitter, and the RFC 3550 estimate.
// Together they separate "the network is bursty" from "we sent unevenly".

import { useCallback, useMemo } from 'react';
import uPlot from 'uplot';
import type { RunResult } from '@/engine/types';
import { UplotChart } from './Uplot';
import { CanvasSurface, EmptyNote } from './Canvas';
import { useChartTheme, withAlpha, type ChartPalette } from './palette';

export interface JitterViewProps {
  run: RunResult;
  height?: number;
}

interface JitterData {
  data: uPlot.AlignedData;
  deltas: number[];
  sendJitter: number;
  recvJitter: number;
}

/** Mean absolute deviation of consecutive intervals (a robust jitter proxy). */
function intervalJitter(times: number[]): number {
  if (times.length < 3) return 0;
  const gaps: number[] = [];
  for (let i = 1; i < times.length; i++) gaps.push(times[i] - times[i - 1]);
  const mean = gaps.reduce((a, b) => a + b, 0) / gaps.length;
  const mad = gaps.reduce((a, b) => a + Math.abs(b - mean), 0) / gaps.length;
  return mad / 1000; // us -> ms
}

function prepare(run: RunResult): JitterData {
  const xs: number[] = [];
  const ys: (number | null)[] = [];
  const deltas: number[] = [];
  const sendTimes: number[] = [];
  const recvTimes: number[] = [];
  let prevRtt: number | null = null;
  for (const p of run.packets) {
    if (p.rttMs == null || (p.cls !== 'ok' && p.cls !== 'late')) {
      prevRtt = null;
      continue;
    }
    const t = (p.sentUs - run.startedAtUs) / 1e6;
    sendTimes.push(p.sentUs);
    recvTimes.push(p.sentUs + p.rttMs * 1000);
    if (prevRtt != null) {
      const d = Math.abs(p.rttMs - prevRtt);
      xs.push(t);
      ys.push(d);
      deltas.push(d);
    } else {
      xs.push(t);
      ys.push(null);
    }
    prevRtt = p.rttMs;
  }
  return {
    data: [xs, ys] as uPlot.AlignedData,
    deltas,
    sendJitter: intervalJitter(sendTimes),
    recvJitter: intervalJitter(recvTimes),
  };
}

export function JitterView({ run, height = 200 }: JitterViewProps) {
  const { palette } = useChartTheme();
  const jd = useMemo(() => prepare(run), [run]);
  const m = run.metrics;

  const options = useMemo<Omit<uPlot.Options, 'width'>>(
    () => ({
      height,
      cursor: { drag: { x: true, y: false }, sync: { key: `run-${run.nodeId}` } },
      legend: { show: false },
      scales: { x: { time: false } },
      axes: [
        {
          stroke: palette.axis,
          grid: { stroke: palette.grid },
          font: '11px system-ui, sans-serif',
          label: 'Elapsed (s)',
          labelFont: '12px system-ui, sans-serif',
        },
        {
          stroke: palette.axis,
          grid: { stroke: palette.grid },
          font: '11px system-ui, sans-serif',
          label: '|Δrtt| (ms)',
          labelFont: '12px system-ui, sans-serif',
        },
      ],
      series: [
        { label: 't' },
        {
          label: '|Δrtt|',
          stroke: palette.late,
          width: 1.25,
          fill: withAlpha(palette.late, 0.12),
          points: { show: false },
          spanGaps: false,
        },
      ],
    }),
    [palette, height, run.nodeId],
  );

  const histDraw = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) =>
      drawDeltaHist(ctx, w, h, jd.deltas, palette),
    [jd.deltas, palette],
  );

  if (jd.deltas.length === 0) {
    return <EmptyNote label="Not enough consecutive echoes for jitter." />;
  }

  const maxBar = Math.max(m.rfc3550JitterMs, m.avgJitterMs, jd.sendJitter, jd.recvJitter, 0.001);

  return (
    <figure style={{ margin: 0 }}>
      <div style={{ overflowX: 'auto' }}>
        <UplotChart options={options} data={jd.data} ariaLabel="Round-trip jitter over time" />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(160px, 1fr) minmax(200px, 1.3fr)',
          gap: 16,
          marginTop: 8,
          alignItems: 'stretch',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <div style={{ fontSize: 12, color: palette.textMuted, marginBottom: 2 }}>
            |Δrtt| distribution
          </div>
          <CanvasSurface height={110} draw={histDraw} ariaLabel="Delta RTT histogram" />
        </div>
        <div>
          <div style={{ fontSize: 12, color: palette.textMuted, marginBottom: 4 }}>
            Jitter breakdown (ms)
          </div>
          <JitterBar label="RFC 3550" value={m.rfc3550JitterMs} max={maxBar} color={palette.accent} />
          <JitterBar label="Mean |Δrtt|" value={m.avgJitterMs} max={maxBar} color={palette.late} />
          <JitterBar
            label="Upstream (send pacing)"
            value={jd.sendJitter}
            max={maxBar}
            color={palette.lostUp}
          />
          <JitterBar
            label="Downstream (arrival)"
            value={jd.recvJitter}
            max={maxBar}
            color={palette.lostDown}
          />
        </div>
      </div>
    </figure>
  );
}

function JitterBar({
  label,
  value,
  max,
  color,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
}) {
  const { palette } = useChartTheme();
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '3px 0' }}>
      <span style={{ flex: '0 0 150px', fontSize: 12, color: palette.text }}>{label}</span>
      <span
        style={{
          flex: 1,
          height: 12,
          background: palette.grid,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <span
          style={{ display: 'block', width: `${pct}%`, height: '100%', background: color }}
        />
      </span>
      <span
        style={{
          flex: '0 0 52px',
          textAlign: 'right',
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
          color: palette.text,
        }}
      >
        {value.toFixed(2)}
      </span>
    </div>
  );
}

function drawDeltaHist(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  deltas: number[],
  palette: ChartPalette,
) {
  if (deltas.length === 0) return;
  const max = Math.max(...deltas, 1e-6);
  const nb = Math.min(30, Math.max(6, Math.round(Math.sqrt(deltas.length))));
  const binW = max / nb;
  const bins = new Array<number>(nb).fill(0);
  for (const d of deltas) bins[Math.min(nb - 1, Math.floor(d / binW))] += 1;
  const maxCount = Math.max(1, ...bins);
  const padB = 14;
  const plotH = h - padB;
  const bw = w / nb;
  ctx.fillStyle = withAlpha(palette.late, 0.6);
  bins.forEach((c, i) => {
    const bh = (c / maxCount) * plotH;
    ctx.fillRect(i * bw + 0.5, plotH - bh, Math.max(1, bw - 1), bh);
  });
  ctx.fillStyle = palette.textMuted;
  ctx.font = '10px system-ui, sans-serif';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillText('0', 1, plotH + 2);
  ctx.textAlign = 'right';
  ctx.fillText(`${max.toFixed(1)} ms`, w - 1, plotH + 2);
}
